/**
 * Doctor Analytics Dashboard
 * Comprehensive analytics and performance metrics for doctors
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Star as StarIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Analytics as AnalyticsIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  DateRange as DateIcon,
  LocalHospital as HospitalIcon,
  Psychology as PsychologyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LocalPharmacy as PharmacyIcon,
  Description as DocumentIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Notifications as NotificationIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

// API Services
import { doctorAnalyticsApi } from '../../services/api/doctorManagement';

const DoctorAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await doctorAnalyticsApi.getAnalytics();
      setAnalyticsData(response);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTimeRangeLabel = (range) => {
    switch (range) {
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 90 Days';
      case '1y': return 'Last Year';
      default: return 'Last 30 Days';
    }
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue, description }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend === 'up' ? (
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
            ) : (
              <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
            )}
            <Typography variant="caption" color={trend === 'up' ? 'success.main' : 'error.main'}>
              {trendValue}
            </Typography>
          </Box>
        )}
        {description && (
          <Typography variant="caption" color="text.secondary" display="block">
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children, height = 300 }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ height }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );

  // Mock data for charts (in real implementation, this would come from the API)
  const appointmentTrendsData = [
    { month: 'Jan', appointments: 45, revenue: 4500 },
    { month: 'Feb', appointments: 52, revenue: 5200 },
    { month: 'Mar', appointments: 48, revenue: 4800 },
    { month: 'Apr', appointments: 61, revenue: 6100 },
    { month: 'May', appointments: 55, revenue: 5500 },
    { month: 'Jun', appointments: 67, revenue: 6700 }
  ];

  const patientSatisfactionData = [
    { name: 'Excellent', value: 45, color: '#4caf50' },
    { name: 'Good', value: 35, color: '#8bc34a' },
    { name: 'Average', value: 15, color: '#ff9800' },
    { name: 'Poor', value: 5, color: '#f44336' }
  ];

  const consultationTypesData = [
    { name: 'Initial Consultation', value: 40 },
    { name: 'Follow-up', value: 35 },
    { name: 'Emergency', value: 15 },
    { name: 'Telehealth', value: 10 }
  ];

  if (loading && !analyticsData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Analytics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time Range"
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAnalyticsData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => toast.info('Export feature coming soon!')}
          >
            Export
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Appointments"
            value={analyticsData?.total_appointments || 0}
            icon={<ScheduleIcon />}
            color="primary.main"
            trend="up"
            trendValue="+12%"
            description="This month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Patients"
            value={analyticsData?.total_patients || 0}
            icon={<PeopleIcon />}
            color="secondary.main"
            trend="up"
            trendValue="+8%"
            description="Active patients"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`$${analyticsData?.monthly_revenue || 0}`}
            icon={<ReceiptIcon />}
            color="success.main"
            trend="up"
            trendValue="+15%"
            description="This month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Rating"
            value={analyticsData?.average_rating || 0}
            icon={<StarIcon />}
            color="warning.main"
            trend="up"
            trendValue="+0.2"
            description="Out of 5.0"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Appointment Trends */}
        <Grid item xs={12} md={8}>
          <ChartCard title="Appointment Trends">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={appointmentTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Area type="monotone" dataKey="appointments" stackId="1" stroke="#8884d8" fill="#8884d8" />
                <Area type="monotone" dataKey="revenue" stackId="2" stroke="#82ca9d" fill="#82ca9d" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Patient Satisfaction */}
        <Grid item xs={12} md={4}>
          <ChartCard title="Patient Satisfaction">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={patientSatisfactionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {patientSatisfactionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Consultation Types */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Consultation Types">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={consultationTypesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <ChartTooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Revenue Trends */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Revenue Trends">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={appointmentTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Performance Metrics */}
      <Grid container spacing={3}>
        {/* Top Performing Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Metrics
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Appointment Completion Rate"
                    secondary="98.5%"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Patient Retention Rate"
                    secondary="87.2%"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Response Time"
                    secondary="< 2 hours"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="No-show Rate"
                    secondary="12.3%"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="New appointment scheduled"
                    secondary="2 hours ago"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="New patient registered"
                    secondary="4 hours ago"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ReceiptIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Payment received"
                    secondary="6 hours ago"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="New review received"
                    secondary="1 day ago"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Goals and Targets */}
      <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Goals & Targets
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Monthly Appointments Target
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="body2">
                75 of 100 appointments (75%)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Revenue Target
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={90} 
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="body2">
                $9,000 of $10,000 (90%)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Patient Satisfaction
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={95} 
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="body2">
                4.8 of 5.0 (95%)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default DoctorAnalytics;


















